import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isYouTube, parseYouTubeId, loadYouTubeAPI } from "../lib/youtube";

/**
 * MediaPlayer: unified HTML5 <video> / YouTube IFrame player.
 * Props:
 *  - src: video URL (mp4 or YouTube)
 *  - poster: thumbnail (mp4 only)
 *  - onTimeUpdate(currentTime: number)
 *  - onDuration(duration: number)
 *  - autoPlay
 * Ref methods: seekTo(seconds), play(), pause()
 */
const MediaPlayer = forwardRef(({ src, poster, onTimeUpdate, onDuration, autoPlay = true }, ref) => {
  const videoRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytContainerRef = useRef(null);
  const pollRef = useRef(null);
  const [ytReady, setYtReady] = useState(false);

  const youtube = isYouTube(src);
  const videoId = youtube ? parseYouTubeId(src) : null;

  useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (youtube && ytPlayerRef.current) {
        ytPlayerRef.current.seekTo(seconds, true);
        ytPlayerRef.current.playVideo?.();
      } else if (videoRef.current) {
        videoRef.current.currentTime = seconds;
        videoRef.current.play?.().catch(() => {});
      }
    },
    play: () => {
      if (youtube && ytPlayerRef.current) ytPlayerRef.current.playVideo?.();
      else videoRef.current?.play?.().catch(() => {});
    },
    pause: () => {
      if (youtube && ytPlayerRef.current) ytPlayerRef.current.pauseVideo?.();
      else videoRef.current?.pause?.();
    },
  }), [youtube]);

  // YouTube init
  useEffect(() => {
    if (!youtube || !videoId) return;
    let cancelled = false;

    loadYouTubeAPI().then((YT) => {
      if (cancelled || !ytContainerRef.current) return;
      // Clean any previous instance
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch { /* ignore */ }
        ytPlayerRef.current = null;
      }
      ytPlayerRef.current = new YT.Player(ytContainerRef.current, {
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onReady: (e) => {
            setYtReady(true);
            const dur = e.target.getDuration?.();
            if (dur && onDuration) onDuration(dur);
          },
          onStateChange: () => {
            // no-op, we poll time
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch { /* ignore */ }
        ytPlayerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, youtube]);

  // YouTube poll current time
  useEffect(() => {
    if (!youtube || !ytReady) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      try {
        const p = ytPlayerRef.current;
        if (!p) return;
        const t = p.getCurrentTime?.();
        const d = p.getDuration?.();
        if (typeof t === "number" && onTimeUpdate) onTimeUpdate(t);
        if (typeof d === "number" && d > 0 && onDuration) onDuration(d);
      } catch { /* ignore */ }
    }, 250);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [youtube, ytReady, onTimeUpdate, onDuration]);

  if (youtube) {
    return (
      <div className="w-full h-full max-h-full bg-black flex items-center justify-center overflow-hidden">
        <div
          className="w-full max-w-full max-h-full aspect-video"
          data-testid="yt-player-wrap"
        >
          <div ref={ytContainerRef} className="w-full h-full" />
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      controls
      autoPlay={autoPlay}
      playsInline
      onTimeUpdate={(e) => onTimeUpdate?.(e.currentTarget.currentTime)}
      onLoadedMetadata={(e) => {
        if (!isNaN(e.currentTarget.duration)) onDuration?.(e.currentTarget.duration);
      }}
      className="w-full h-full max-h-full object-contain bg-black"
      data-testid="video-element"
    />
  );
});

MediaPlayer.displayName = "MediaPlayer";
export default MediaPlayer;
